/**
 * Typed API client with centralised error handling.
 *
 * - Network / timeout errors → navigates to /error/503
 * - Caller-initiated aborts (component unmount) → thrown as-is, no redirect
 * - Persistent 500 errors → navigates to /error/500, then throws
 * - Transient 5xx (502/503/504) → thrown, allowing callers to retry
 * - 4xx errors → thrown so callers can handle them (e.g. 401 → re-auth)
 *
 * Injects the Keycloak id-token as a Bearer header on every request (matches
 * the auth pattern used by apiService, userService, etc.).
 */

import { getIdToken } from './keycloak';

/** Navigate imperatively from outside React components. */
let _navigate: ((path: string) => void) | null = null;

export function setApiNavigator(navigateFn: (path: string) => void): void {
  _navigate = navigateFn;
}

function redirectToError(path: string): void {
  if (_navigate) {
    _navigate(path);
  } else {
    // Fallback for cases where navigator is not yet registered
    window.location.href = path;
  }
}

export interface ApiClientOptions extends RequestInit {
  /** Timeout in milliseconds (default: 30 000 ms) */
  timeoutMs?: number;
}

/**
 * Wraps `fetch` with:
 *  - Keycloak Bearer token injection
 *  - configurable timeout with abort tracking
 *  - automatic redirect to /error/503 on network failure or timeout-triggered abort
 *  - lets callers handle 502/503/504 (transient), redirects only on persistent 500
 *  - throws for 4xx so callers can decide how to handle
 */
export async function apiClient(url: string, options: ApiClientOptions = {}): Promise<Response> {
  const { timeoutMs = 30_000, headers: callerHeaders, ...fetchOptions } = options;

  const controller = new AbortController();
  let isTimeoutAbort = false;
  const timeoutId = setTimeout(() => {
    isTimeoutAbort = true;
    controller.abort();
  }, timeoutMs);

  // Track whether abort originated externally (caller unmount/navigation)
  let isCallerAbort = false;
  if (fetchOptions.signal) {
    fetchOptions.signal.addEventListener('abort', () => {
      isCallerAbort = true;
      controller.abort();
    });
  }

  // Inject Keycloak auth token; fall back gracefully if not yet available
  let authHeader: Record<string, string> = {};
  try {
    const token = await getIdToken();
    if (token) {
      authHeader = { Authorization: `Bearer ${token}` };
    }
  } catch {
    // Not authenticated — let the request proceed unauthenticated; the server will 401
  }

  const mergedHeaders = {
    ...authHeader,
    ...(callerHeaders as Record<string, string> | undefined),
  };

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
      headers: mergedHeaders,
    });

    clearTimeout(timeoutId);

    // Let transient 5xx errors (502, 503, 504) be handled by callers
    // Only redirect on persistent 500 errors
    if (response.status === 500) {
      redirectToError('/error/500');
      throw new Error(`Server error ${response.status}`);
    } else if (response.status >= 502 && response.status < 600) {
      // Throw transient server errors so callers can retry
      const err = new Error(`Server error ${response.status}`);
      (err as any).status = response.status;
      throw err;
    }

    return response;
  } catch (err: unknown) {
    clearTimeout(timeoutId);

    const isAbort = err instanceof DOMException && err.name === 'AbortError';
    const isNetwork = err instanceof TypeError && err.message.toLowerCase().includes('fetch');

    // Redirect only on timeout-initiated aborts and network errors
    // Never redirect on caller-initiated aborts (component unmount) or server errors
    if ((isAbort && isTimeoutAbort) || isNetwork) {
      redirectToError('/error/503');
    } else if (isAbort && isCallerAbort) {
      // Caller-initiated abort (e.g., component unmount) — don't redirect
      // Just rethrow so cleanup can proceed
    }

    throw err;
  }
}
